import { Clock, DownloadIcon, FolderArchive, Youtube } from 'lucide-react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from 'src/components/ui/button/button';
import { Href } from 'src/components/ui/href';
import { useRequestFilesArchivedMessage } from 'src/features/requests/request-files-archived-copy';
import { useFormatter } from 'src/services/i18n';

/**
 * Shown where an archived file would have played: it states that the files
 * are no longer available, in the same words as every download control that
 * is dark for the same reason.
 */
const ArchivedMediaMessage: React.FC = () => {
  const archivedMessage = useRequestFilesArchivedMessage();

  return (
    <div
      className={
        'tw-flex tw-aspect-video tw-h-full tw-items-center tw-justify-center tw-rounded-md tw-bg-neutral-50 tw-text-text-strong'
      }
    >
      <div className={'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-4'}>
        <FolderArchive size={80} className={'tw-text-text-inactive'} />
        <p className={'tw-max-w-96 tw-text-center tw-font-medium'}>{archivedMessage}</p>
      </div>
    </div>
  );
};

const UnarchivedMediaMessage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div
      className={
        'tw-flex tw-aspect-video tw-h-full tw-items-center tw-justify-center tw-rounded-md tw-bg-neutral-50 tw-text-text-strong'
      }
    >
      <div className={'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-4'}>
        <Clock size={80} className={'tw-text-text-inactive'} />
        <p className={'tw-font-bold'}>{t('Please wait..')}</p>
        <p className={'tw-max-w-64 tw-text-center tw-text-text-inactive'}>
          {t('System is currently unarchiving your file (it might take up to 24 hours)')}
        </p>
      </div>
    </div>
  );
};

const ReadyToDownloadMediaMessage: React.FC<{
  expiryDate: string;
  downloadUrl: string;
}> = ({ expiryDate, downloadUrl }) => {
  const { t } = useTranslation();
  const { formatDate } = useFormatter();

  return (
    <div
      className={
        'tw-flex tw-aspect-video tw-h-full tw-items-center tw-justify-center tw-rounded-md tw-bg-neutral-50 tw-text-text-strong'
      }
    >
      <div className={'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-p-4'}>
        <Youtube size={80} className={'tw-text-text-inactive'} />
        <p className={'tw-font-bold'}>{t('Your file was unarchived')}</p>
        <p className={'tw-text-text-inactive'}>
          <Trans>
            <span>
              You have {formatDate(expiryDate)} to download your content, until it’s archived.
            </span>
          </Trans>
        </p>
        <Button asChild variant={'brand'} size={'iconStartResponsive'}>
          <Href href={downloadUrl} target={'_blank'} variant={'unset'}>
            <DownloadIcon size={20} />
            <span>{t('Download')}</span>
          </Href>
        </Button>
      </div>
    </div>
  );
};

export { ArchivedMediaMessage, UnarchivedMediaMessage, ReadyToDownloadMediaMessage };
